import { Box, Heading, Icon, Grid, Stack, Flex, Text } from '@chakra-ui/react';
import { Form, Formik } from 'formik';
import { getDistance } from 'geolib';
import React, { useState, useEffect } from 'react';
import { Marker } from 'react-map-gl';

import { IconLocationPinStart } from 'assets/icons';
import Button from 'components/Button';
import CustomFieldBuilder from 'components/CustomFieldBuilder';
import { FormikSelectAsyncPaginate, FormikTextArea } from 'components/Formik';
import Mapbox from 'components/Mapbox';
import JobPolygon from 'components/Mapbox/JobPolygon';
import JobRadius from 'components/Mapbox/JobRadius';
import { initialTimeClockFormData, timeClockFormValidationSchema } from 'containers/EmployeeTimeClock/schema';
import { getUserLocation, getRoundUpTime } from 'containers/EmployeeTimeClock/shared/helper';
import FEATURES from 'global/features';
import {
  ICustomFieldItem,
  ICustomFormSearchQuery,
  useFetchPaginatedCustomFields,
} from 'hooks/customField/useFetchPaginatedCustomFields';
import { fetchPaginatedJobs } from 'hooks/jobs/useFetchPaginatedJobs';
import { fetchPaginatedTasks } from 'hooks/task/useFetchPaginatedTasks';
import { IClockIn, useClockIn } from 'hooks/timeClock/useClockIn';
import { useFeatureList } from 'store/useFeatureStore';
import { useUserObject } from 'store/useUserStore';
import {
  transformCustomFieldsToPostData,
  hasCustomFieldAccess,
  transformCustomFieldsToFormData,
} from 'utils/customFieldsUtils';
import { getAsyncSelectLoadOptions, getTimezoneOffsetInHM } from 'utils/helpers';
import toaster from 'utils/toaster';
import yup from 'utils/yup';

interface LatLng {
  latitude: number;
  longitude: number;
}

const AddClockInTime: React.FC<unknown> = () => {
  const formikRef = React.useRef<any>(null);

  const [customFieldsQuery, setCustomFieldsQuery] = useState<ICustomFormSearchQuery>({
    page: 1,
    page_size: 500,
    module: 'timesheets',
    active: '1',
    display_on: 'clock_in',
  });
  const [clockInFormData, setClockInFormData] = useState<typeof initialTimeClockFormData>(initialTimeClockFormData);

  const [viewport, setViewport] = useState({
    latitude: 37.82931,
    longitude: -122.486958,
    zoom: 14,
  });
  const [userLocation, setUserLocation] = useState<{ latitude: number | null; longitude: number | null }>({
    latitude: null,
    longitude: null,
  });
  const [validationSchema, setValidationSchema] =
    useState<typeof timeClockFormValidationSchema>(timeClockFormValidationSchema);

  const userStore: any = useUserObject();
  const featureList: any = useFeatureList();

  const { status: clockInStatus, mutate: mutateClockIn } = useClockIn();
  const { data: customFieldsData } = useFetchPaginatedCustomFields(customFieldsQuery, {
    onSuccess: (data: any) => {
      if (Array.isArray(data?.payload) && data?.payload.length) {
        const customFields = transformCustomFieldsToFormData(data?.payload);

        setClockInFormData((prevFormData: typeof initialTimeClockFormData) => {
          const formValues = formikRef?.current?.values || prevFormData;
          return { ...formValues, ...customFields };
        });
      }
    },
  });

  useEffect(() => {
    if (userStore?.userInfo?.locationTracking) {
      getUserLocation(geoLocateSuccess);
    }

    const schema: any = {
      ...timeClockFormValidationSchema,
      job_id:
        featureList[FEATURES.jobs] &&
        (userStore?.companyInfo?.requiresJob
          ? yup.string().required({ msg: 'You are required to select a job!' }).nullable(false)
          : yup.string()),
    };

    setValidationSchema(schema);
    // eslint-disable-next-line
  }, []);

  const geoLocateSuccess = (position: GeolocationPosition) => {
    setViewport({
      latitude: position.coords.latitude,
      longitude: position.coords.longitude,
      zoom: 14,
    });
    setUserLocation({
      latitude: position.coords.latitude,
      longitude: position.coords.longitude,
    });
  };

  const employeeGeoLocationClockIn = (position: GeolocationPosition, values: typeof initialTimeClockFormData) => {
    const userLatLng = { latitude: position?.coords.latitude, longitude: position?.coords.longitude };
    const jobLatLng = { latitude: values?.job_id?.latitude, longitude: values?.job_id?.longitude };
    const dist = getDistance(userLatLng, jobLatLng, 0.1);
    if (dist <= values?.job_id?.radius) {
      clockIn(values, userLatLng);
    } else {
      toaster.error({
        description:
          'You cannot clock in! Your current location ' +
          'shows you are outside of the work area. You need to be within the job area boundaries to clock in.',
      });
    }
  };

  const handleFormSubmit = async (values: typeof initialTimeClockFormData) => {
    if (
      userStore?.userInfo?.locationTracking &&
      userStore?.companyInfo?.geofenceBoundedClockIn &&
      values?.job_id?.requireGeofence
    ) {
      getUserLocation(position => {
        employeeGeoLocationClockIn(position, values);
      });
      return;
    }

    if (userStore?.userInfo?.locationTracking) {
      if (userLocation.latitude && userLocation.longitude) {
        clockIn(values, userLocation as LatLng);
      } else {
        toaster.error({
          description: "You cannot clock in! Please enable your browser's location services to proceed.",
        });
      }
      return;
    }
    clockIn(values);
  };

  const clockIn = (values: typeof initialTimeClockFormData, position?: LatLng) => {
    const hasAccess = hasCustomFieldAccess();

    const postData: IClockIn = {
      user_id: userStore?.user_id,
      job_id: values.job_id.id,
      task_id: values.task_id.id,
      notes: values.notes,
      offset: getTimezoneOffsetInHM(),
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      clock_in_time: getRoundUpTime('clock_in'),
      ...(hasAccess ? { custom_fields: transformCustomFieldsToPostData(customFieldsData?.payload || [], values) } : {}),
    };
    if (position) {
      postData.latitude = position.latitude;
      postData.longitude = position.longitude;
    }
    mutateClockIn(postData);
  };

  const jobLoadOptions = (search: any, _: any, { page }: any) => {
    return getAsyncSelectLoadOptions(fetchPaginatedJobs, { page, search, job_status: 1 });
  };

  const taskLoadOptions = (page: number, search: string, jobId: any) => {
    return getAsyncSelectLoadOptions(fetchPaginatedTasks, { page, search, task_status: 1, jobId: jobId });
  };

  const showJobLocation = (jobData: any) => {
    setViewport({
      latitude: jobData.latitude,
      longitude: jobData.longitude,
      zoom: 16,
    });
  };

  const showCurrentLocation = () => {
    if (userLocation.latitude && userLocation.longitude) {
      setViewport({
        latitude: userLocation.latitude,
        longitude: userLocation.longitude,
        zoom: 16,
      });
    }
  };

  return (
    <>
      <Box mx={{ base: 0, sm: 4, md: 6 }} my={{ base: 4, md: 6 }} p="4" bg="white" height="100%" borderRadius="lg">
        <Stack direction="column">
          <Heading as="h6" size="xs" fontWeight="semibold" color="gray.500">
            Welcome {userStore?.userInfo?.firstName}!
          </Heading>
          <Heading fontSize="24px" size="md" fontWeight="semibold">
            What are you working on?
          </Heading>
        </Stack>
        <Box mt="6" maxWidth="1000px" mx="9px">
          <Formik
            initialValues={clockInFormData}
            enableReinitialize
            validationSchema={yup.object().shape(validationSchema)}
            onSubmit={handleFormSubmit}
          >
            {formikBag => {
              formikRef.current = formikBag;
              const { values } = formikBag;
              return (
                <Form noValidate>
                  <Grid templateColumns={{ base: '1fr', md: '1fr 300px' }} gap={6}>
                    <Box display="flex" flexDirection="column" alignItems="center" mb="4">
                      <Box position="relative" width="100%">
                        {featureList[FEATURES.jobs] && userStore?.companyInfo?.requiresJob && (
                          <FormikSelectAsyncPaginate
                            name="job_id"
                            label={'Job Name'}
                            loadOptions={jobLoadOptions}
                            onChangeCallback={(data: any) => {
                              setCustomFieldsQuery(prevQuery => ({
                                ...prevQuery,
                                job_id: data?.id,
                              }));
                            }}
                            selectProps={{
                              isMulti: false,
                              placeholder: 'Select a job',
                              getOptionLabel: (option: any) => option?.name,
                              getOptionValue: (option: any) => option.id,
                            }}
                            formControlProps={{ isRequired: true }}
                          />
                        )}
                      </Box>

                      {userStore?.companyInfo?.requiresTask && userStore?.companyInfo?.requiresJob && (
                        <FormikSelectAsyncPaginate
                          name="task_id"
                          label="Task Name"
                          loadOptions={(search: any, _: any, { page }: any) =>
                            taskLoadOptions(page, search, values.job_id?.id)
                          }
                          selectProps={{
                            isMulti: false,
                            placeholder: 'Select a task',
                            getOptionLabel: (option: any) => option?.name,
                            getOptionValue: (option: any) => option.id,
                            isDisabled: !values.job_id,
                          }}
                        />
                      )}
                      <FormikTextArea name="notes" label="Note" />

                      <Flex justifyContent="space-between" width="100%" mb="4">
                        {userLocation?.latitude && userLocation?.longitude ? (
                          <Button variant="primary-ghost" onClick={showCurrentLocation} size="sm">
                            See User location
                          </Button>
                        ) : null}
                        {values.job_id?.longitude && values.job_id?.latitude ? (
                          <Button variant="primary-ghost" onClick={() => showJobLocation(values.job_id)} size="sm">
                            See Job location
                          </Button>
                        ) : null}
                      </Flex>

                      {Array.isArray(customFieldsData?.payload) && customFieldsData?.payload?.length ? (
                        <Box width="100%">
                          <Text textTransform="uppercase" fontWeight="semibold" color="gray.600" mb="3">
                            Custom Fields
                          </Text>
                          {customFieldsData?.payload.map((customField: ICustomFieldItem) => {
                            return (
                              <div key={customField.id}>
                                <CustomFieldBuilder customField={customField as ICustomFieldItem} />
                              </div>
                            );
                          })}
                        </Box>
                      ) : null}
                    </Box>
                    {userStore?.userInfo?.locationTracking && (
                      <Box width="100%" height="385px" maxHeight="400px">
                        <Mapbox
                          {...viewport}
                          style={{ width: '100%', height: '100%' }}
                          onMove={(evt: any) => {
                            if (evt?.viewState) {
                              const { latitude, longitude, zoom } = evt.viewState;
                              setViewport({ latitude, longitude, zoom });
                            }
                          }}
                        >
                          {userLocation?.latitude && userLocation?.longitude ? (
                            <Marker
                              latitude={userLocation.latitude}
                              longitude={userLocation.longitude}
                              offset={[0, -5]}
                            >
                              <Icon as={IconLocationPinStart} fontSize="3xl" />
                            </Marker>
                          ) : null}

                          {values.job_id?.requireGeofence && !values.job_id?.custom_geofence ? (
                            <JobRadius
                              latitude={values.job_id.latitude}
                              longitude={values.job_id.longitude}
                              radius={values.job_id.radius}
                            />
                          ) : null}

                          {values.job_id?.requireGeofence &&
                          values.job_id?.custom_geofence &&
                          Array.isArray(values.job_id?.geofence_coordinates) ? (
                            <JobPolygon coordinates={values.job_id?.geofence_coordinates || []} />
                          ) : null}
                        </Mapbox>
                      </Box>
                    )}
                  </Grid>

                  <Button type="submit" colorScheme="secondary" isLoading={clockInStatus === 'loading'}>
                    Clock In
                  </Button>
                  <Button
                    _hover={{ bg: 'gray.200' }}
                    _active={{ bg: 'gray.200' }}
                    ml="5"
                    onClick={() => undefined}
                    variant="ghost"
                  >
                    Cancel
                  </Button>
                </Form>
              );
            }}
          </Formik>
        </Box>
      </Box>
    </>
  );
};
export default AddClockInTime;
